<template>
  <div class="llm-config-page">
    <!-- 顶栏与下方主栏+预览同宽、整体居中，避免顶栏全宽而中间留白 -->
    <div class="llm-layout-shell">
    <!-- 顶部栏（与原型：左标题/编号/状态/时间，右 历史版本 / 发布 / 保存） -->
    <div class="page-header">
      <div class="header-left">
        <div class="header-left-inner">
          <h1 class="page-title">{{ llmTitle || '—' }}</h1>
          <span v-if="sceneId" class="header-scene-code">{{ headerSceneCode }}</span>
          <span
            v-if="releaseText"
            class="header-status-pill"
            :class="{ 'is-published': storeSceneRelease === 1 }"
          >{{ releaseText }}</span>
          <span class="header-meta">创建：{{ createTime || '—' }}</span>
          <span class="header-meta">更新：{{ updateTimeStr }}</span>
        </div>
      </div>
      <div class="header-actions">
        <LlmHistoryVersions
          :scene-id="sceneId"
          @restore="applyLlmFromHistoryRestore"
        />
        <Button class="header-btn-publish" @click="onClickPublish">发布</Button>
        <Button class="header-btn-save" @click="handleSave">保存</Button>
      </div>
    </div>

      <!-- 主内容区：左侧 Tab + 右侧 Simulation Demo（话术调试器 iframe） -->
    <div class="page-body-split">
      <div class="page-body-main">
        <div class="page-body-main-inner">
          <!-- View Design：嵌套 Tabs 时外层须设置 name，各 TabPane 须 tab 指向该 name，否则内层 TabPane 会被外层一并收集进主导航 -->
          <Tabs v-model="activeMainTab" class="llm-main-tabs" name="llmMain" :animated="false">
            <TabPane label="提示词配置" name="prompt" tab="llmMain">
              <div class="llm-tab-pane-scroll">
                <LlmPromptConfigTab
                  :form="form"
                  :prompt-mode.sync="promptMode"
                  :flow-section-order.sync="flowSectionOrder"
                  :custom-flow-sections="customFlowSections"
                  :flow-section-titles="flowSectionTitles"
                  :expanded="expanded"
                  :flow-guide-expanded="flowGuideExpanded"
                  :fixed-title-show-input="fixedTitleShowInput"
                  :custom-title-show-input="customTitleShowInput"
                  :intent-tag="intentTag"
                  :get-role-info-text-for-prompt="getRoleInfoTextForPrompt"
                  :memory-enabled="voiceCall.callControl.memory_enabled === true"
                />
              </div>
            </TabPane>

            <TabPane label="模型与语音" name="voice" tab="llmMain">
              <div class="llm-tab-pane-scroll">
                <LlmVoiceCallTab
                  :voice-call="voiceCall"
                  @clone-voice-saved="handleSave"
                />
              </div>
            </TabPane>

            <TabPane label="意向与标签" name="intent" tab="llmMain">
              <div class="llm-tab-pane-scroll">
                <LlmIntentionTagTab :intent-tag="intentTag" :scene-name="storeSceneName" />
              </div>
            </TabPane>

            <TabPane label="通话控制" name="callControl" tab="llmMain">
              <div class="llm-tab-pane-scroll">
                <LlmCallControlTab
                  :call-control="voiceCall.callControl"
                  :preset-mode="voiceCall.presetMode"
                  :g2-locked="isG2Mode"
                  :customer-info-params="form.customerInfoParams"
                  :little-assistant="form.littleAssistant"
                  :external-apis-json="form.externalApisJson"
                  :external-apis-form="form.externalApisForm"
                  :event-triggers="form.eventTriggers"
                  :intent-tag="intentTag"
                />
              </div>
            </TabPane>
          </Tabs>
        </div>
      </div>
      <aside class="page-body-aside">
        <LlmSimulationDemo :voice-call="voiceCall" />
      </aside>
    </div>
    </div>

    <Modal
      v-model="publishModalVisible"
      title="发布"
      class-name="llm-publish-modal"
      :footer-hide="true"
      width="420"
    >
      <p class="publish-modal-tip">是否应用于进行中的任务？</p>
      <div class="publish-modal-btns">
        <Button @click="publishModalVisible = false">取消</Button>
        <Button
          class="header-btn-publish"
          :loading="publishLoading"
          @click="doPublish(false)"
        >只发布</Button>
        <Button
          type="primary"
          :loading="publishLoading"
          @click="doPublish(true)"
        >发布并应用所有任务</Button>
      </div>
    </Modal>

  </div>
</template>

<script>
import { mapState } from 'vuex'
import moment from 'moment'
import {
  publishSceneA,
  addCrmFormA,
  updateCrmFormA,
  getCrmFormA,
  getCrmFormListPageA,
  addCrmFormSceneA,
  updateCrmFormSceneA,
  getCrmFormSceneListA,
} from '@/server/api'
import LlmSimulationDemo from './components/simulation-demo/index.vue'
import LlmHistoryVersions from './components/history-versions/index.vue'
import LlmPromptConfigTab from './components/prompt-config/index.vue'
import LlmIntentionTagTab from './components/intention-tag/index.vue'
import LlmVoiceCallTab from './components/voice-call/index.vue'
import LlmCallControlTab from './components/call-control/index.vue'
import {
  DEFAULT_PROMPT_SECTION_ORDER,
  PROMPT_MODE,
  ROLE_PERSONA_SECTION_KEY,
} from './constants/flowSection'
import {
  applyBusinessFlowFromLlmsettings,
  applyCustomerInfoParamsFromSnapshot,
  applyLittleAssistantFromSettings,
  applySpeechParamsFromParameterConfig,
  buildCustomerInfoParamsSnapshot,
  buildLittleAssistantSnapshot,
  buildParameterConfigSnapshot,
  createDefaultCustomerInfoParams,
  createDefaultLittleAssistant,
  createDefaultSpeechParams,
  getCustomRow,
  getFixedSectionTitle,
  getFlowSectionPromptValue,
  isCustomFlowKey,
  normalizePromptSectionOrder,
  snapshotPromptMode,
} from './utils/flowSectionHelpers'
import {
  buildTabbedLlmsettingsDocument,
  parseLlmsettingsV2,
  validateLlmsettingsSchema,
} from './utils/llmsettingsSchema'
import { coerceLlmsettingsForUi } from './utils/llmsettingsMigrate'
import {
  VOICE_ENGINE,
  getPresetCatalogEntry,
  resolveVoiceEngine,
  getDefaultPresetId,
  getCloneVendor,
  findEngineCloneVoiceById,
  isPresetCatalogVoiceId,
  getStaleVoiceAuditionMessage,
  TRIPLE_CLONE_VENDOR,
} from './constants/voicePresets'
import {
  PRESET_MODE,
  createDefaultTripleAccess,
  buildTripleAccessSnapshot,
  applyTripleAccessToForm,
  resolveVoicePresetModeFromSettings,
  resolveSelectedLlmModelName,
  isG2TripleAccessMode,
} from './utils/tripleAccessHelpers'
import {
  applyCallControlToForm,
  buildCallControlSnapshot,
  createDefaultCallControl,
} from './utils/callControlHelpers'
import {
  applyExternalApisGroupFromSnapshot,
  buildExternalApisGroupSnapshot,
} from './utils/externalApiHelpers'
import {
  applyEventTriggersFromSnapshot,
  buildEventTriggersSnapshot,
} from './utils/eventTriggerHelpers'
import {
  applyIntentTagFromSnapshot,
  buildIntentTagSnapshot,
  buildCrmFormPayload,
  createDefaultIntentTagState,
  findCrmFormByNameFromListResponse,
  hasForcedExtractFormData,
  mergeCrmFormUpdatePayload,
  parseCrmFormIdFromResponse,
  parseCrmFormSceneListFromResponse,
  toCrmFormIdNumber,
} from './utils/intentTagHelpers'

/** sceneJson 可 JSON 序列化；避免再 import lodash/cloneDeep 与别处重名 */
function cloneSceneJson(obj) {
  if (obj == null || typeof obj !== 'object') return obj
  try {
    return JSON.parse(JSON.stringify(obj))
  } catch (e) {
    return { ...obj }
  }
}

/** 大模型话术保存/发布时写入 sceneJson 内 llm-omini.trunk_ip；优先 manualIp，否则按域名选测/正式 */
const LLM_SCENE_TRUNK_CONFIG = {
  manualIp: '',
  testIp: '172.17.98.104:13000',
  prodIp: '172.17.98.109:13000',
}

function isLlmTrunkTestHostname() {
  if (typeof window === 'undefined') return false
  const h = String(window.location.hostname || '').toLowerCase()
  return h.includes('bj') || h.includes('localhost')
}

function pickLlmTrunkIp(config = LLM_SCENE_TRUNK_CONFIG) {
  const manual = config.manualIp != null ? String(config.manualIp).trim() : ''
  if (manual) return manual
  return isLlmTrunkTestHostname()
    ? String(config.testIp || '').trim()
    : String(config.prodIp || '').trim()
}

export default {
  name: 'LlmConfig',
  components: {
    LlmSimulationDemo,
    LlmHistoryVersions,
    LlmPromptConfigTab,
    LlmIntentionTagTab,
    LlmVoiceCallTab,
    LlmCallControlTab,
  },
  data() {
    return {
      activeMainTab: 'prompt',
      promptMode: PROMPT_MODE.FREE,
      llmTitle: '',
      releaseText: '',
      sceneId: 0,
      createTime: '',
      form: {
        roleName: '',
        identities: [],
        communicationStyles: [],
        roleDesc: '',
        customPrompt: '',
        customTools: '',
        externalApisJson: [],
        externalApisForm: [],
        eventTriggers: [],
        littleAssistant: createDefaultLittleAssistant(),
        customerInfoParams: createDefaultCustomerInfoParams(),
        businessBackground: '',
        businessTarget: '',
        businessProcess: '',
        opening: '',
        dialogueExample: '',
        dialogueConstraint: '',
        questionConstraint: '',
      },
      /** 提示词配置 Tab 内区块顺序（含 rolePersona + 业务块） */
      flowSectionOrder: [...DEFAULT_PROMPT_SECTION_ORDER],
      /** 用户「增加内容」添加的模块：{ id, title, body } */
      customFlowSections: [],
      /** 固定模块标题覆盖（key → 文案），缺省用 FLOW_SECTION_META[key].title */
      flowSectionTitles: {},
      /** 固定模块标题：true 显示输入框 */
      fixedTitleShowInput: {},
      /** 自定义块标题：true 显示输入框，false 显示文本（回车/失焦后） */
      customTitleShowInput: {},
      dialogueCount: 6,
      expanded: {
        [ROLE_PERSONA_SECTION_KEY]: false,
        roleInfo: false,
        roleDesc: false,
        opening: false,
        businessBackground: false,
        businessTarget: false,
        businessProcess: false,
        // intentTag: false,
        // dialogueExample: false,
        dialogueConstraint: false,
        questionConstraint: false,
        qa: false,
        dialogue: false,
        flow: false,
        promptPreview: false,
      },
      intentTag: createDefaultIntentTagState(),
      /** 模型与语音（写入 llmsettings.voice_settings） */
      voiceCall: {
        voiceUiTab: 'preset',
        presetMode: PRESET_MODE.ENGINE,
        tripleAccess: createDefaultTripleAccess(),
        clonedVoices: [],
        voiceEngine: VOICE_ENGINE.ALIYUN,
        presetVoiceId: getDefaultPresetId(VOICE_ENGINE.ALIYUN),
        speechParams: createDefaultSpeechParams(),
        ttsSpeechParams: createDefaultSpeechParams(),
        asrSpeechParams: createDefaultSpeechParams(),
        callControl: createDefaultCallControl(),
      },
      updateTimeStr: '—',
      publishModalVisible: false,
      publishLoading: false,
      /** 业务模块「配置指引」折叠态（仅前端 UI，文案在 FLOW_SECTION_META） */
      flowGuideExpanded: {},
    };
  },
  computed: {
    headerSceneCode() {
      return this.sceneId ? `${this.sceneId}` : '';
    },
    // 从 Vuex 场景模块映射需要的字段
    ...mapState('scene', {
      storeSceneId: 'sceneId',
      storeSceneName: 'sceneName',
      storeSceneRelease: 'isRelease',
      storeSceneCreateAt: 'createAt',
      storeSceneUpdateAt: 'updateAt',
      storeScene: 'scene',
    }),
    isG2Mode() {
      return isG2TripleAccessMode(this.voiceCall)
    },
  },
  mounted() {

  },
  watch: {
    storeSceneId: {
      immediate: true,
      handler(id) {
        if (!id) return;
        this.sceneId = id;
      },
    },
    storeSceneName: {
      immediate: true,
      handler(name) {
        if (!name) return;
        this.llmTitle = name;
      },
    },
    storeSceneRelease: {
      immediate: true,
      handler(isRelease) {
        if (isRelease < 0) return;
        this.releaseText = isRelease === 1 ? '已发布' : '未发布';
      },
    },
    storeSceneCreateAt: {
      immediate: true,
      handler(createAt) {
        if (!createAt) {
          this.createTime = '';
          return;
        }
        this.createTime = moment(createAt * 1000).format('YYYY-MM-DD HH:mm');
      },
    },
    storeSceneUpdateAt: {
      immediate: true,
      handler(ts) {
        if (!ts) {
          this.updateTimeStr = '—';
          return;
        }
        this.updateTimeStr = moment(ts * 1000).format('YYYY-MM-DD HH:mm');
      },
    },
    storeScene: {
      immediate: true,
      handler(sceneJson) {
        if (!sceneJson || typeof sceneJson !== 'object') return;
        const llmsettings =
          sceneJson.llmsettings && typeof sceneJson.llmsettings === 'object'
            ? sceneJson.llmsettings
            : {};
        this.applyLlmToForm(llmsettings);
      },
    },
  },
  methods: {
    /**
     * 将 scene.llmsettings 写入表单；非 v2 时仅基于 llmsettings 内存迁移后展示。
     * 保存/发布只写 llmsettings，不读写 scene.settings.llm。
     */
    applyLlmToForm(llmsettings = {}) {
      const v2 = coerceLlmsettingsForUi(llmsettings);
      const raw = v2 && typeof v2 === 'object' ? v2 : {};
      const check = validateLlmsettingsSchema(raw);
      if (!check.valid) {
        return false;
      }
      if (check.empty) {
        this.promptMode = PROMPT_MODE.FREE;
        return true;
      }

      const tabs = parseLlmsettingsV2(raw);
      const promptCfg = tabs.prompt_config || {};
      const callTab = tabs.call_control_settings || {};
      const intentTab = tabs.intent_and_tags || {};

      const lsRs =
        promptCfg.role_setting && typeof promptCfg.role_setting === 'object'
          ? promptCfg.role_setting
          : null;

      if (lsRs && lsRs.nickname != null) {
        this.form.roleName = String(lsRs.nickname);
      }
      if (lsRs && Array.isArray(lsRs.identity) && lsRs.identity.length) {
        this.form.identities = lsRs.identity.map((item) => ({
          text: String((item && item.role_name) || '').trim(),
          active: !!(item && item.selection),
        }));
      }
      if (lsRs && Array.isArray(lsRs.communication_style) && lsRs.communication_style.length) {
        this.form.communicationStyles = lsRs.communication_style.map((item) => ({
          text: String((item && item.communication_style_name) || '').trim(),
          active: !!(item && item.selection),
        }));
      }
      if (lsRs && lsRs.description != null) {
        this.form.roleDesc = String(lsRs.description);
      }

      this.form.customPrompt =
        promptCfg.custom_prompt != null ? String(promptCfg.custom_prompt) : '';

      this.form.customTools =
        callTab.custom_tools != null ? String(callTab.custom_tools) : '';

      const externalApisGroup = applyExternalApisGroupFromSnapshot(callTab, this.form.customTools)
      this.form.externalApisJson = externalApisGroup.json
      this.form.externalApisForm = externalApisGroup.form

      this.form.eventTriggers = applyEventTriggersFromSnapshot(callTab.event_triggers)

      this.form.littleAssistant = applyLittleAssistantFromSettings(callTab.assistant_settings);
      this.form.customerInfoParams = applyCustomerInfoParamsFromSnapshot(
        callTab.customer_info_params,
      );

      if (Array.isArray(promptCfg.business_flow) && promptCfg.business_flow.length) {
        const biz = applyBusinessFlowFromLlmsettings(
          promptCfg.business_flow,
          this.customFlowSections,
        );
        Object.keys(biz.formPatch).forEach((k) => {
          this.form[k] = biz.formPatch[k];
        });
        this.customFlowSections = biz.customFlowSections;
        this.flowSectionOrder = normalizePromptSectionOrder(
          biz.flowSectionOrder,
          biz.customFlowSections,
        );
      }

      applyIntentTagFromSnapshot(intentTab, this.intentTag);

      this.applyVoiceSettingsFromLlmsettings(tabs.voice_settings || {});

      const cc = callTab.call_control;
      if (cc && typeof cc === 'object') {
        if (!this.voiceCall.callControl || typeof this.voiceCall.callControl !== 'object') {
          this.$set(this.voiceCall, 'callControl', createDefaultCallControl());
        }
        applyCallControlToForm(cc, this.voiceCall.callControl, {
          isCustomPreset:
            (this.voiceCall.presetMode || PRESET_MODE.ENGINE) === PRESET_MODE.CUSTOM,
        });
      } else if (!this.voiceCall.callControl) {
        this.$set(this.voiceCall, 'callControl', createDefaultCallControl());
      }

      this.promptMode = PROMPT_MODE.FREE;
      return true;
    },
    applyLlmFromHistoryRestore(payload) {
      if (!payload || typeof payload !== 'object') {
        this.applyLlmToForm({});
        return;
      }
      const ok = this.applyLlmToForm(payload.llmsettings || {});
      if (ok) {
        this.activeMainTab = 'prompt';
      }
    },
    /** 角色信息一段文本（姓名、身份、沟通风格），与保存 prompt 中「角色信息」块一致 */
    getRoleInfoTextForPrompt() {
      const roleInfoLines = [];
      if (this.form.roleName != null && String(this.form.roleName).trim())
        roleInfoLines.push(`姓名：${String(this.form.roleName).trim()}`);
      // if (this.form.gender != null && String(this.form.gender).trim())
      //   roleInfoLines.push(`性别：${String(this.form.gender).trim()}`);
      const identityTexts = (this.form.identities || [])
        .filter((i) => {
          if (!i) return false;
          if (typeof i === 'object') return !!i.active;
          return true;
        })
        .map((i) => (i && typeof i === 'object' ? (i.text ?? i.label ?? '') : String(i)))
        .filter(Boolean);
      if (identityTexts.length)
        roleInfoLines.push(`身份：${identityTexts.join('、')}`);
      const styleTexts = (this.form.communicationStyles || [])
        .filter((c) => {
          if (!c) return false;
          if (typeof c === 'object') return !!c.active;
          return true;
        })
        .map((c) => (c && typeof c === 'object' ? (c.text ?? c.label ?? '') : String(c)))
        .filter(Boolean);
      if (styleTexts.length)
        roleInfoLines.push(`沟通风格：${styleTexts.join('、')}`);
      return roleInfoLines.join('\n');
    },
    /**
     * 克隆音色列表项归一化（持久化字段：voice_id、name、vendor、audio_file_path、audio_file_name、gender）
     * 兼容旧数据的 id / engine / clone_mode / tts_engine（不再写入 tts_vendor）
     */
    normalizeVoiceCloneEntry(x) {
      if (!x || typeof x !== 'object') return null;
      const voiceId = String(
        x.voice_id != null && String(x.voice_id).trim() !== ''
          ? x.voice_id
          : x.id != null
            ? x.id
            : '',
      ).trim();
      if (!voiceId) return null;
      const legacyEngine = x.engine != null ? String(x.engine).trim() : '';
      const legacyCloneMode = x.clone_mode != null ? String(x.clone_mode).trim() : '';
      let vendor =
        x.vendor != null && String(x.vendor).trim() !== '' ? String(x.vendor).trim() : '';
      const isTriple =
        vendor === TRIPLE_CLONE_VENDOR ||
        legacyEngine === VOICE_ENGINE.TRIPLE ||
        legacyCloneMode === 'triple';
      if (!vendor) {
        if (isTriple) {
          vendor = getCloneVendor(VOICE_ENGINE.TRIPLE);
        } else if (legacyEngine) {
          vendor = getCloneVendor(resolveVoiceEngine(legacyEngine));
        } else {
          vendor = getCloneVendor(VOICE_ENGINE.ALIYUN);
        }
      }
      const audio_file_path = String(
        x.audio_file_path != null ? x.audio_file_path : '',
      ).trim();
      const name = String(x.name != null ? x.name : '');
      const audio_file_name = String(
        x.audio_file_name != null ? x.audio_file_name : name,
      ).trim();
      const entry = {
        voice_id: voiceId,
        name,
        vendor,
        audio_file_path,
        selection: false,
      };
      if (audio_file_name) entry.audio_file_name = audio_file_name;
      if (x.gender != null && String(x.gender).trim() !== '') {
        entry.gender = String(x.gender).trim();
      }
      if (isTriple) {
        const productLine = String(
          x.product_line != null ? x.product_line : x.productLine != null ? x.productLine : '',
        ).trim();
        entry.product_line = productLine;
      }
      return entry;
    },
    /** 列表暂不支持点击选中，统一清除 selection */
    sanitizeCloneVoicesSelection(list) {
      if (!Array.isArray(list) || list.length === 0) return;
      list.forEach((x) => {
        if (x) this.$set(x, 'selection', false);
      });
    },
    /** preset_voice_id 是否为系统预设音色（非克隆返回的长 voice_id） */
    isCatalogPresetVoiceId(id, engine) {
      const s = String(id || '').trim();
      if (!s) return false;
      if (engine != null && String(engine).trim() !== '') {
        return !!getPresetCatalogEntry(s, engine);
      }
      return isPresetCatalogVoiceId(s);
    },
    /** 从 llmsettings.voice_settings 恢复到 voiceCall（字段名与导出 JSON 一致） */
    applyVoiceSettingsFromLlmsettings(vs) {
      if (!vs || typeof vs !== 'object') return;

      const at = vs.active_tab;
      if (at && typeof at === 'object') {
        const tab = at.tab || at.current || at.selected;
        if (tab === 'system_preset' || tab === 'preset') {
          this.voiceCall.voiceUiTab = 'preset';
        } else if (tab === 'custom_voices' || tab === 'mine') {
          this.voiceCall.voiceUiTab = 'mine';
        }
        const customVoiceRows = Array.isArray(at.custom_voices) ? at.custom_voices : [];
        if (customVoiceRows.length > 0) {
          this.voiceCall.clonedVoices = customVoiceRows
            .map((row) => this.normalizeVoiceCloneEntry(row))
            .filter(Boolean);
          this.sanitizeCloneVoicesSelection(this.voiceCall.clonedVoices);
        }
      }

      const sp = vs.system_preset;
      if (sp && typeof sp === 'object') {
        if (sp.voice_engine != null && String(sp.voice_engine).trim() !== '') {
          this.voiceCall.voiceEngine = resolveVoiceEngine(sp.voice_engine);
        }
        if (sp.preset_voice_id != null && String(sp.preset_voice_id).trim() !== '') {
          this.voiceCall.presetVoiceId = String(sp.preset_voice_id);
        }
      }

      if (vs.triple_access && typeof vs.triple_access === 'object') {
        if (!this.voiceCall.tripleAccess || typeof this.voiceCall.tripleAccess !== 'object') {
          this.$set(this.voiceCall, 'tripleAccess', createDefaultTripleAccess());
        }
        applyTripleAccessToForm(vs.triple_access, this.voiceCall.tripleAccess, {
          clonedVoices: this.voiceCall.clonedVoices,
        });
      } else if (!this.voiceCall.tripleAccess) {
        this.$set(this.voiceCall, 'tripleAccess', createDefaultTripleAccess());
      }

      this.voiceCall.presetMode = resolveVoicePresetModeFromSettings({
        systemPreset: sp,
        tripleAccess: vs.triple_access,
      });

      const pc = vs.parameter_config;
      if (pc && typeof pc === 'object') {
        applySpeechParamsFromParameterConfig(pc, this.voiceCall);
      }
    },
    buildSystemPresetPayload(vc) {
      const engineSnap = resolveVoiceEngine(vc.voiceEngine);
      let presetIdSnap = String(vc.presetVoiceId != null ? vc.presetVoiceId : '').trim();
      const isCatalog = this.isCatalogPresetVoiceId(presetIdSnap, engineSnap);
      const cloneRow = findEngineCloneVoiceById(vc.clonedVoices, presetIdSnap, engineSnap);
      if (!isCatalog && !cloneRow) {
        presetIdSnap = getDefaultPresetId(engineSnap);
      }
      const presetCatalog = isCatalog ? getPresetCatalogEntry(presetIdSnap, engineSnap) : null;
      const normalizedClone = cloneRow ? this.normalizeVoiceCloneEntry(cloneRow) : null;
      const systemPreset = {
        mode: PRESET_MODE.ENGINE,
        voice_engine: engineSnap,
        preset_voice_id: presetIdSnap,
      };
      if (presetCatalog && presetCatalog.vendor && presetCatalog.voice_id) {
        systemPreset.vendor = presetCatalog.vendor;
        systemPreset.voice_id = presetCatalog.voice_id;
      } else if (normalizedClone) {
        systemPreset.vendor = normalizedClone.vendor;
        systemPreset.voice_id = normalizedClone.voice_id;
      }
      const audioPath =
        (presetCatalog && presetCatalog.audio_file_path) ||
        (normalizedClone && normalizedClone.audio_file_path) ||
        '';
      if (String(audioPath).trim()) {
        systemPreset.audio_file_path = String(audioPath).trim();
      }
      return systemPreset;
    },
    /** 写入 llmsettings.voice_settings（parameter_config.list 为 name/key；call_control 为蛇形字段） */
    buildVoiceSettingsSnapshot() {
      const vc = this.voiceCall;
      if (!vc.callControl || typeof vc.callControl !== 'object') {
        this.$set(vc, 'callControl', createDefaultCallControl());
      }

      const isCustomPreset = (vc.presetMode || PRESET_MODE.ENGINE) === PRESET_MODE.CUSTOM;
      const systemPreset = this.buildSystemPresetPayload(vc);
      systemPreset.selected = !isCustomPreset;

      const snapshot = {
        model_name: resolveSelectedLlmModelName({
          presetMode: vc.presetMode,
          voiceEngine: vc.voiceEngine,
          llm: vc.tripleAccess && vc.tripleAccess.llm,
        }),
        active_tab: {
          custom_voices: (vc.clonedVoices || [])
            .map((row) => this.normalizeVoiceCloneEntry(row))
            .filter(Boolean),
        },
        system_preset: systemPreset,
        triple_access: buildTripleAccessSnapshot(vc.tripleAccess, {
          selected: isCustomPreset,
          clonedVoices: vc.clonedVoices,
        }),
        parameter_config: buildParameterConfigSnapshot({
          speechParams: vc.speechParams,
          ttsSpeechParams: vc.ttsSpeechParams,
          asrSpeechParams: vc.asrSpeechParams,
        }),
        call_control: buildCallControlSnapshot(vc.callControl, { isCustomPreset }),
      };
      return snapshot;
    },
    /**
     * 聚合大模型全部配置，仅写入 scene.llmsettings（与后端/导出约定字段名）
     */
    buildLlmSettingsSnapshot() {
      const identity = (this.form.identities || [])
        .map((i) => {
          const role_name =
            i && typeof i === 'object'
              ? String((i.text ?? i.label ?? '') || '').trim()
              : String(i || '').trim();
          return {
            role_name,
            selection: !!(i && typeof i === 'object' && i.active),
          };
        })
        .filter((row) => row.role_name);

      const communication_style = (this.form.communicationStyles || [])
        .map((c) => {
          const communication_style_name =
            c && typeof c === 'object'
              ? String((c.text ?? c.label ?? '') || '').trim()
              : String(c || '').trim();
          return {
            communication_style_name,
            selection: !!(c && typeof c === 'object' && c.active),
          };
        })
        .filter((row) => row.communication_style_name);

      const business_flow = (this.flowSectionOrder || [])
        .filter((k) => k !== ROLE_PERSONA_SECTION_KEY)
        .map((k) => {
        if (isCustomFlowKey(k)) {
          const row = getCustomRow(this.customFlowSections, k);
          const title = row ? String(row.title || '').trim() || '自定义' : '自定义';
          const content = row && row.body != null ? String(row.body) : '';
          return { title, content };
        }
        return {
          title: getFixedSectionTitle(this.flowSectionTitles, k),
          content:
            getFlowSectionPromptValue(this.form, this.intentTag, k) != null
              ? String(getFlowSectionPromptValue(this.form, this.intentTag, k))
              : '',
        };
      });

      const intentSnapshot = buildIntentTagSnapshot(this.intentTag, this.storeSceneName);

      return buildTabbedLlmsettingsDocument({
        role_setting: {
          identity,
          communication_style,
          nickname: this.form.roleName != null ? String(this.form.roleName) : '',
          description: this.form.roleDesc != null ? String(this.form.roleDesc) : '',
        },
        business_flow,
        custom_prompt:
          this.form.customPrompt != null ? String(this.form.customPrompt) : '',
        prompt_mode: snapshotPromptMode(this.promptMode),
        voice_settings: this.buildVoiceSettingsSnapshot(),
        intent_mode: intentSnapshot.intent_mode,
        intent_tags: intentSnapshot.intent_tags,
        extract_form: intentSnapshot.extract_form,
        rating_rules: intentSnapshot.rating_rules,
        custom_tools:
          this.form.customTools != null ? String(this.form.customTools) : '',
        ...buildExternalApisGroupSnapshot(this.form.externalApisJson, this.form.externalApisForm),
        event_triggers: buildEventTriggersSnapshot(this.form.eventTriggers),
        assistant_settings: buildLittleAssistantSnapshot(this.form.littleAssistant),
        customer_info_params: buildCustomerInfoParamsSnapshot(this.form.customerInfoParams),
      });
    },
    /** 与 pickLlmTrunkIp 共用，供 ensureThirdLlmSceneInSceneJson 写入 nodes[].trunk_ip */
    resolveLlmTrunkIp() {
      return pickLlmTrunkIp(LLM_SCENE_TRUNK_CONFIG)
    },
    /**
     * 保存/发布前保证 sceneJson.scenes[0] 含 llm-omini 节点（与流程侧约定：F00001/L00001、agentid、trunk_ip 等）。
     * 若已有 llm-omini 则合并字段，避免误删同场景其它节点。
     */
    ensureThirdLlmSceneInSceneJson(scene) {
      const next = cloneSceneJson(scene)
      const sid = this.sceneId != null && this.sceneId !== '' ? Number(this.sceneId) : NaN
      const agentid = String(Number.isFinite(sid) ? sid : this.sceneId != null ? this.sceneId : '')
      const nameVal =
        this.storeSceneName != null && String(this.storeSceneName).trim() !== ''
          ? String(this.storeSceneName).trim()
          : '双工大模型'
      const trunk_ip = this.resolveLlmTrunkIp()

      if (!Array.isArray(next.scenes)) next.scenes = []

      let flow = next.scenes[0]
      if (!flow || typeof flow !== 'object') {
        flow = { id: 'F00001', name: '双工大模型', nodes: [] }
        next.scenes[0] = flow
      }
      if (flow.id == null || String(flow.id).trim() === '') flow.id = 'F00001'
      if (flow.name == null || String(flow.name).trim() === '') flow.name = '双工大模型'
      if (!Array.isArray(flow.nodes)) flow.nodes = []

      const thirdBase = {
        id: 'L00001',
        type: 'llm-omini',
        name: nameVal,
        agentid,
        trunk_ip,
        tag: '',
        top: 200,
        left: 0,
      }

      const byLid = flow.nodes.findIndex((n) => n && n.type === 'llm-omini' && String(n.id) === 'L00001')
      const anyThird = flow.nodes.findIndex((n) => n && n.type === 'llm-omini')
      if (byLid >= 0) {
        flow.nodes[byLid] = { ...cloneSceneJson(flow.nodes[byLid]), ...thirdBase }
      } else if (anyThird >= 0) {
        flow.nodes[anyThird] = { ...cloneSceneJson(flow.nodes[anyThird]), ...thirdBase }
      } else {
        flow.nodes = [cloneSceneJson(thirdBase), ...flow.nodes]
      }
      return next
    },
    /**
     * 强制收取 · 信息采集有字段时：
     * 1) 创建/更新 CRM 自定义表单（已有 form_id 则 update，不再 add）
     * 2) 将表单绑定到当前话术（已绑定则不再调 scene/add）
     * 失败不阻断话术保存，仅提示。
     */
    syncCrmFormFromExtract() {
      if (!hasForcedExtractFormData(this.intentTag)) {
        return Promise.resolve()
      }
      const payload = buildCrmFormPayload(this.intentTag, this.storeSceneName)
      const applyFormId = (formId) => {
        if (formId) {
          this.$set(this.intentTag, 'extractFormId', formId)
        }
      }
      // 更新前拉取已有表单，合并推送设置，避免话术保存清空 pushApi / push_grade 等
      const withPreservedPushSettings = (nextPayload, existingFormHint) => {
        if (!nextPayload || !nextPayload.id) return Promise.resolve(nextPayload)
        return getCrmFormA({ id: nextPayload.id })
          .then((res) => {
            const data = res && res.code === 200 ? res.data : null
            const existing = data || existingFormHint
            return existing ? mergeCrmFormUpdatePayload(nextPayload, existing) : nextPayload
          })
          .catch(() =>
            existingFormHint
              ? mergeCrmFormUpdatePayload(nextPayload, existingFormHint)
              : nextPayload,
          )
      }
      const runUpdateOrAdd = (nextPayload) => {
        const request = nextPayload.id ? updateCrmFormA(nextPayload) : addCrmFormA(nextPayload)
        return request.then((res) => {
          if (!res || res.code !== 200) {
            const msg = (res && (res.msg || res.message)) || '创建采集表单失败'
            this.$Message && this.$Message.warning && this.$Message.warning(msg)
            return ''
          }
          const formId = parseCrmFormIdFromResponse(res) || nextPayload.id || ''
          applyFormId(formId)
          return formId
        })
      }

      const syncForm = () => {
        // 已有 form_id：合并推送配置后再更新
        if (payload.id) {
          return withPreservedPushSettings(payload).then((merged) => runUpdateOrAdd(merged))
        }
        // 创建前按名称查重
        return getCrmFormListPageA({
          name: payload.name,
          page: 1,
          size: 20,
        }).then((listRes) => {
          const existed = findCrmFormByNameFromListResponse(listRes, payload.name)
          const formId = existed ? toCrmFormIdNumber(existed.id) : null
          if (formId != null) {
            applyFormId(formId)
            // 已存在则不再创建，仅回填 id 并同步字段（同时保留推送设置）
            return withPreservedPushSettings(
              {
                id: formId,
                name: payload.name,
                content: payload.content,
              },
              existed,
            ).then((merged) => runUpdateOrAdd(merged))
          }
          return runUpdateOrAdd(payload)
        })
      }

      return syncForm()
        .then((formId) => this.bindCrmFormToScene(formId))
        .catch(() => {
          this.$Message &&
            this.$Message.warning &&
            this.$Message.warning('同步采集表单失败，话术仍会继续保存')
        })
    },
    /**
     * 将采集表单绑定到当前话术。
     * 已存在绑定关系时不再调用 scene/add；仅当绑定的表单 id 变化时走 scene/update。
     */
    bindCrmFormToScene(formId) {
      const sceneId = Number(this.sceneId || 0)
      const customFormId = toCrmFormIdNumber(formId || this.intentTag.extractFormId)
      if (!sceneId || customFormId == null) {
        return Promise.resolve()
      }

      return getCrmFormSceneListA({ scene_id: sceneId })
        .then((listRes) => {
          const list = parseCrmFormSceneListFromResponse(listRes)
          const existing = list.find((item) => item && item.id != null) || list[0]
          if (existing && existing.id != null) {
            // 已绑定：不再调 scene/add
            if (toCrmFormIdNumber(existing.custom_form_id) === customFormId) {
              return
            }
            // 绑定的表单变了，更新绑定关系
            return updateCrmFormSceneA({
              id: existing.id,
              scene_id: sceneId,
              custom_form_id: customFormId,
            }).then((res) => {
              if (res && res.code !== 200) {
                const msg = (res && (res.msg || res.message)) || '更新采集表单绑定失败'
                this.$Message && this.$Message.warning && this.$Message.warning(msg)
              }
            })
          }
          // 首次绑定
          return addCrmFormSceneA({
            scene_id: sceneId,
            custom_form_id: customFormId,
          }).then((res) => {
            if (res && res.code !== 200) {
              const msg = (res && (res.msg || res.message)) || '绑定采集表单失败'
              this.$Message && this.$Message.warning && this.$Message.warning(msg)
            }
          })
        })
        .catch(() => {
          this.$Message &&
            this.$Message.warning &&
            this.$Message.warning('绑定采集表单失败，话术仍会继续保存')
        })
    },
    /**
     * 【跨模块回写】大模型「振铃时长」→ 流程设置 · 一般设置「呼叫超时时长」
     *
     * 背景：
     * - 大模型页「通话控制」新增了振铃时长，字段名 call_timeout，落在 scene.llmsettings.*.call_control
     * - 流程设置 · 一般设置里原有「呼叫超时时长」，字段名同样是 call_timeout，落在 scene.settings.general
     * - 这是 scene 根上 settings 的外部字段（不在 llmsettings 内）；两边文案不同、配置域不同，但运行时都读 call_timeout
     *
     * 因此保存大模型时必须把振铃时长同步写到 settings.general.call_timeout，否则外面一般设置仍是旧值。
     * 只改 general.call_timeout，不碰 general 其它项；不单独再调 settings 局部保存，随本次 saveScene 一并落库。
     * saveScene → allotScene → setting/setSettings 会刷新 vuex，流程设置页无需手动刷新即可看到新值。
     */
    syncGeneralCallTimeoutFromRinging(scene) {
      if (!scene || typeof scene !== 'object') return scene

      const callControl =
        this.voiceCall && this.voiceCall.callControl && typeof this.voiceCall.callControl === 'object'
          ? this.voiceCall.callControl
          : null
      const raw = callControl && callControl.call_timeout != null ? Number(callControl.call_timeout) : NaN
      // 与通话控制表单校验一致：至少 1 秒；无效则不覆盖外面已有配置
      if (!Number.isFinite(raw) || raw < 1) return scene
      const callTimeout = Math.max(1, Math.floor(raw))

      const settings =
        scene.settings && typeof scene.settings === 'object' ? { ...scene.settings } : {}
      const general =
        settings.general && typeof settings.general === 'object' ? { ...settings.general } : {}
      // 外部字段：scene.settings.general.call_timeout（流程设置 · 呼叫超时时长）
      general.call_timeout = callTimeout
      settings.general = general
      scene.settings = settings
      return scene
    },
    /**
     * 保存大模型配置。
     * 主路径：写入 scene.llmsettings + scenes 节点。
     * 副作用：回写外部字段 settings.general.call_timeout（见 syncGeneralCallTimeoutFromRinging）。
     */
    handleSave(options = {}) {
      const { showSuccessToast = true } = options
      const baseScene = this.storeScene;
      if (!baseScene || typeof baseScene !== 'object') {
        this.$Message && this.$Message.error && this.$Message.error('话术数据未加载，无法保存');
        return Promise.reject(new Error('no scene'));
      }

      return this.syncCrmFormFromExtract().then(() => {
        const llmsettingsPayload = this.buildLlmSettingsSnapshot();
        const newScene = this.ensureThirdLlmSceneInSceneJson({
          ...cloneSceneJson(baseScene),
          id: this.sceneId || baseScene.id,
          version: baseScene.version || '2.2',
          name: this.storeSceneName || baseScene.name,
          llmsettings: llmsettingsPayload,
        });
        // 【跨模块】同步外部「流程设置 · 一般设置 · 呼叫超时时长」= 大模型「振铃时长」
        this.syncGeneralCallTimeoutFromRinging(newScene);

        return this.$store
          .dispatch('scene/saveSceneRelation', {
            oldScene: baseScene,
            newScene,
          })
          .then(() => this.$store.dispatch('scene/saveScene', newScene))
          .then(() => {
            if (showSuccessToast) {
              this.$Message && this.$Message.success && this.$Message.success('保存成功');
            }
          })
          .catch(() => {
            this.$Message && this.$Message.error && this.$Message.error('保存失败，请重试');
            return Promise.reject(new Error('save failed'));
          });
      });
    },
    onClickPublish() {
      if (!this.sceneId) {
        this.$Message.error('话术未加载，无法发布');
        return;
      }
      this.publishModalVisible = true;
    },
    doPublish(applyToRunningTasks) {
      const id = Number(this.sceneId || 0);
      if (!id) return;
      this.publishLoading = true;
      // 与保存一致：先落库（含 scenes[0].nodes llm-omini 字段），再调发布接口
      this.handleSave({ showSuccessToast: false })
        .then(() =>
          publishSceneA({
            sceneId: id,
            applyAll: applyToRunningTasks ? 'yes' : 'no',
          }),
        )
        .then((res) => {
          if (res && res.code === 200) {
            this.$Message.success('发布成功');
            this.publishModalVisible = false;
            // 仅刷新发布状态等元数据（getSceneDetail 不会覆盖 store 中的 sceneJson）
            return this.$store.dispatch('scene/getSceneDetail');
          }
          this.$Message.error((res && res.msg) || '发布失败');
          return Promise.reject(new Error('publish failed'));
        })
        .catch((err) => {
          if (err && err.message === 'publish failed') {
            return;
          }
          if (err && err.message === 'save failed') {
            return;
          }
          this.$Message.error('发布失败');
        })
        .finally(() => {
          this.publishLoading = false;
        });
    },
  },
};
</script>

<style lang="scss" scoped>
.llm-config-page {
  height: 100vh;
  max-height: 100vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  background: #f5f6f8;
  padding: 0 24px 16px;
}

/*
 * 与下方双栏同宽并整体居中：1200（主栏 max-width）+ 12（gap）+ 400（预览 max）
 * 勿对 shell 使用 fit-content：Tab 仅渲染当前 pane 时内在最小宽度变小，整体会被挤窄。
 */
.llm-layout-shell {
  flex: 1 1 auto;
  min-height: 0;
  width: 100%;
  max-width: 1612px;
  margin: 0 auto;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
}

.page-header {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px 16px;
  padding: 14px 20px;
  background: #fff;
  margin: 0 0 12px;
  border-radius: 0;
  border-bottom: 1px solid #e8e8e8;
  .header-left {
    flex: 1;
    min-width: 0;
  }
  .header-left-inner {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 12px 16px;
    row-gap: 8px;
  }
  .page-title {
    font-size: 20px;
    font-weight: 600;
    margin: 0;
    color: #1a1a1a;
  }
  .header-scene-code {
    font-size: 13px;
    color: #8c8c8c;
    line-height: 1.4;
  }
  .header-status-pill {
    display: inline-block;
    padding: 2px 10px;
    border-radius: 10px;
    font-size: 12px;
    line-height: 1.5;
    color: #595959;
    background: #e8edf3;
    &.is-published {
      color: #389e0d;
      background: #f6ffed;
    }
  }
  .header-meta {
    font-size: 12px;
    color: #8c8c8c;
    line-height: 1.4;
  }
  .header-actions {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px 12px;
    flex-shrink: 0;
  }
  .header-btn-publish {
    border: none;
    color: #fff;
    background: #1fb486;
    padding: 0 18px;
    height: 34px;
    border-radius: 4px;
    font-size: 14px;
    &:hover {
      background: #17a077;
      color: #fff;
    }
  }
  .header-btn-save {
    border: none;
    color: #fff;
    background: #0066a1;
    padding: 0 18px;
    height: 34px;
    border-radius: 4px;
    font-size: 14px;
    &:hover {
      background: #005286;
      color: #fff;
    }
  }
}

.publish-modal-tip {
  margin: 0 0 16px;
  color: #595959;
  font-size: 14px;
}
.publish-modal-btns {
  display: flex;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 8px;
}

.page-body-split {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  gap: 12px;
  align-items: stretch;
  width: 100%;
  box-sizing: border-box;
  overflow: hidden;
}

/* 占满顶栏下方剩余视口高度，高度由 flex 约束而非随内容撑开 */
.page-body-main {
  flex: 1 1 auto;
  min-width: 0;
  min-height: 0;
  max-width: 1200px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.page-body-main-inner {
  flex: 1 1 auto;
  width: 100%;
  max-width: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
  border: 1px solid #e2e8f0;
}

/* 与左侧主栏同高：行向 stretch + aside 内 flex 列，iframe 在 LlmSimulationDemo 内 flex:1 撑满 */
.page-body-aside {
  align-self: stretch;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  min-height: 0;
  width: min(400px, 35vw);
  min-width: 280px;
  box-sizing: border-box;
}

@media (max-width: 1100px) {
  .llm-layout-shell {
    max-width: none;
  }
  .llm-config-page {
    height: 100vh;
    max-height: 100vh;
  }
  .page-body-split {
    flex-direction: column;
  }
  .page-body-main {
    width: 100%;
    max-width: 100%;
    flex: 1 1 auto;
    min-height: 0;
  }
  .page-body-main-inner {
    max-width: 100%;
  }
  .page-body-aside {
    position: static;
    width: 100%;
    min-width: 0;
    max-width: 100%;
    /* 纵向堆叠时给 iframe 保底高度，避免无明确高度时塌陷 */
    min-height: min(560px, calc(100vh - 120px));
  }
}

/* 只作用于顶层 Tabs 的导航条，勿用 ::v-deep .ivu-tabs-bar 匹配内层（音色页里「系统预设/三段式接入」），否则会像多出一排主导航 */
.llm-main-tabs {
  flex: 1 1 auto;
  width: 100%;
  min-height: 0;
  height: 100%;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
  padding: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  ::v-deep > .ivu-tabs-content {
    flex: 1 1 auto;
    min-height: 0;
    height: 0;
    overflow: hidden;
    background: #f0f3f9;
  }

  ::v-deep > .ivu-tabs-content > .ivu-tabs-tabpane {
    height: 100%;
    overflow: hidden;
  }

  ::v-deep > .ivu-tabs-bar {
    flex-shrink: 0;
    margin-bottom: 0;
    padding: 5px 0;
    border-bottom: 1px solid #e2e8f0;
    background: #f8f9fc;
  }

  ::v-deep > .ivu-tabs-bar .ivu-tabs-nav-container {
    margin-bottom: 0;
  }

  ::v-deep > .ivu-tabs-bar .ivu-tabs-nav-wrap {
    margin-bottom: 0;
  }

  ::v-deep > .ivu-tabs-bar .ivu-tabs-ink-bar {
    display: none;
  }

  ::v-deep > .ivu-tabs-bar .ivu-tabs-nav {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  ::v-deep > .ivu-tabs-bar .ivu-tabs-nav .ivu-tabs-tab {
    margin: 0;
    padding: 0 16px;
    border: none;
    border-radius: 6px;
    background: transparent;
    color: #475569;
    font-size: 14px;
    line-height: 30px;
    transition: background 0.2s ease, color 0.2s ease;

    &:hover:not(.ivu-tabs-tab-active) {
      background: #eef1f6;
      color: #262626;
    }

    &.ivu-tabs-tab-active,
    &.ivu-tabs-tab-active:hover {
      background: #5668f0;
      color: #fff;
      font-weight: 500;
    }
  }

  /*
   * 保持主导航 Tabs 关闭内置 animated，以免多 TabPane 占位撑高。
   * View Design 的 TabPane 用内联 visibility 切换，无 tabpane-active 类名；
   * 对 visibility:visible 的面板做淡入 + 略上移，替代横向滑动。
   */
  ::v-deep > .ivu-tabs-content > .ivu-tabs-tabpane[style*='visibility: visible'],
  ::v-deep > .ivu-tabs-content > .ivu-tabs-tabpane[style*='visibility:visible'] {
    animation: llm-main-tab-pane-in 0.42s cubic-bezier(0.33, 1, 0.68, 1) both;
  }
}

/* Tab 内容区：高度与可视区域对齐，仅在此容器内滚动；滚动条默认隐藏，悬停时显示 */
.llm-tab-pane-scroll {
  height: 100%;
  max-height: 100%;
  overflow-x: hidden;
  overflow-y: auto;
  box-sizing: border-box;
  padding: 16px 16px 16px;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: thin;
  scrollbar-color: transparent transparent;

  &:hover {
    scrollbar-color: rgba(0, 0, 0, 0.28) transparent;
  }

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: transparent;
    border-radius: 4px;
  }

  &:hover::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.22);
  }
}

@keyframes llm-main-tab-pane-in {
  from {
    opacity: 0;
    transform: translateY(7px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@media (prefers-reduced-motion: reduce) {
  .llm-main-tabs ::v-deep > .ivu-tabs-content > .ivu-tabs-tabpane[style*='visibility: visible'],
  .llm-main-tabs ::v-deep > .ivu-tabs-content > .ivu-tabs-tabpane[style*='visibility:visible'] {
    animation: none;
  }
}
</style>
